import { FLAG_LABELS, normalizeFlagStatus } from './communicationsFlags';

function toDate(value) {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? new Date() : date;
}

function normalizeReadBy(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

// BL-177 Step 2k: a system note may carry one link (the reservation it reports). Only an in-app path is ever kept.
const SAFE_NOTE_LINK = /^\/(?:dispatch|communications)(?:\?[A-Za-z0-9=&_.-]*)?$/;

export function safeNoteLink(url) {
    return typeof url === 'string' && SAFE_NOTE_LINK.test(url) ? url : null;
}

export function normalizeInternalNotes(notes = []) {
    if (!Array.isArray(notes)) return [];
    return notes
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry, index) => {
            const createdAt = entry.created_at || entry.createdAt || entry.at || null;
            return {
                id: entry.id || `internal-note-${index}`,
                type: entry.type === 'manual' ? 'manual' : 'system',
                text: typeof entry.text === 'string' ? entry.text : '',
                userId: entry.user_id || entry.userId || null,
                userLabel: entry.user_label || entry.userLabel || 'Staff',
                createdAt,
                eventId: entry.event_id || entry.eventId || null,
                linkUrl: safeNoteLink(entry.link_url || entry.linkUrl),
                linkLabel: typeof (entry.link_label || entry.linkLabel) === 'string' ? (entry.link_label || entry.linkLabel) : null,
            };
        })
        .filter((entry) => entry.text.trim())
        .sort((first, second) => {
            const firstTime = Date.parse(first.createdAt) || 0;
            const secondTime = Date.parse(second.createdAt) || 0;
            return firstTime - secondTime;
        });
}

export function toInternalNotesJson(notes = []) {
    return normalizeInternalNotes(notes).map((entry) => ({
        id: entry.id,
        type: entry.type,
        text: entry.text,
        user_id: entry.userId,
        user_label: entry.userLabel,
        created_at: entry.createdAt,
        event_id: entry.eventId,
        ...(entry.linkUrl ? { link_url: entry.linkUrl, link_label: entry.linkLabel } : {}),
    }));
}

export function appendInternalNotes(notes = [], entries = []) {
    return toInternalNotesJson([...normalizeInternalNotes(notes), ...entries]);
}

export function countUnreadInternalNotes(session = {}, userId = null) {
    if (!userId) return 0;
    const readAt = normalizeReadBy(session.internal_notes_read_by)[userId];
    const readTime = Date.parse(readAt) || 0;
    return normalizeInternalNotes(session.internal_notes)
        .filter((entry) => (Date.parse(entry.createdAt) || 0) > readTime)
        .length;
}

export function buildInternalNotesReadBy(readBy = {}, userId = null, at = new Date()) {
    if (!userId) return normalizeReadBy(readBy);
    return {
        ...normalizeReadBy(readBy),
        [userId]: toDate(at).toISOString(),
    };
}

function buildEntry({ type, text, userId, userLabel, createdAt, eventId, suffix }) {
    return {
        id: `${eventId}-${type}-${suffix}`,
        type,
        text,
        userId: userId || null,
        userLabel: userLabel || 'Staff',
        createdAt,
        eventId,
    };
}

export function buildStatusTimelineEntries({
    previousSession = {},
    status,
    active = false,
    note = '',
    userId = null,
    userLabel = 'Staff',
    now = new Date(),
} = {}) {
    const createdAt = toDate(now).toISOString();
    const eventId = `status-${createdAt.replace(/[^0-9]/g, '')}`;
    const nextStatus = normalizeFlagStatus(status);
    const previousActive = Boolean(previousSession?.flag_active);
    const entries = [
        buildEntry({
            type: 'system',
            text: `Status set to ${FLAG_LABELS[nextStatus] || FLAG_LABELS.none}`,
            userId,
            userLabel,
            createdAt,
            eventId,
            suffix: 'status',
        }),
    ];
    if (previousActive !== Boolean(active)) {
        entries.push(buildEntry({
            type: 'system',
            text: active ? 'Marked active' : 'Marked inactive',
            userId,
            userLabel,
            createdAt,
            eventId,
            suffix: 'active',
        }));
    }
    const trimmedNote = typeof note === 'string' ? note.trim() : '';
    if (trimmedNote) {
        entries.push(buildEntry({
            type: 'manual',
            text: trimmedNote,
            userId,
            userLabel,
            createdAt,
            eventId,
            suffix: 'note',
        }));
    }
    return entries;
}

export function buildManualInternalNote({ note = '', userId = null, userLabel = 'Staff', now = new Date() } = {}) {
    const trimmed = typeof note === 'string' ? note.trim() : '';
    if (!trimmed) return null;
    const createdAt = toDate(now).toISOString();
    const eventId = `manual-${createdAt.replace(/[^0-9]/g, '')}`;
    return buildEntry({
        type: 'manual',
        text: trimmed,
        userId,
        userLabel,
        createdAt,
        eventId,
        suffix: 'note',
    });
}
